import { JsonLd } from '@/lib/shared/components/JsonLd';

import { BASE_FRONT_URL } from '@/lib/config';
import Head from 'next/head';

import { hreflangAlternates } from './hreflang';

import { FallbackSeo, PageSeo } from '../model';

type Props = {
  seo: Omit<PageSeo, 'language' | 'path'> | null;
  fallback: FallbackSeo;
  language: string;
  path: `/${string}` | '';
  noFollow?: boolean;
  robots?: string;
  hreflangLocales?: string[];
};

export const SeoEntries = ({
  seo,
  fallback,
  language,
  path,
  noFollow,
  robots,
  hreflangLocales,
}: Props) => {
  const title = seo?.title || fallback.title;
  const description = seo?.description || fallback.description;
  const rawOpenGraphImage = seo?.openGraphImage || fallback.openGraphImage;
  const openGraphImage = rawOpenGraphImage?.startsWith('/')
    ? `${BASE_FRONT_URL}${rawOpenGraphImage}`
    : rawOpenGraphImage;
  const keywords = seo?.keywords || fallback.keywords;
  return (
    <Head>
      <title>{title}</title>
      <meta property="og:image" content={openGraphImage} />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Villacarte" />
      <meta
        property="og:url"
        content={`${BASE_FRONT_URL}/${language}${path}`}
      />
      <link
        rel="canonical"
        href={`${BASE_FRONT_URL}/${language}${path}`}
      ></link>
      <meta
        name="cc:attributionURL"
        content={`${BASE_FRONT_URL}/${language}${path}`}
      />
      {hreflangAlternates({
        origin: BASE_FRONT_URL,
        path,
        locales: hreflangLocales,
      }).map((link) => (
        <link
          key={link.hrefLang}
          rel="alternate"
          href={link.href}
          hrefLang={link.hrefLang}
        />
      ))}
      {robots ? (
        <meta name="robots" content={robots} />
      ) : (
        noFollow && <meta name="robots" content="noindex, nofollow" />
      )}
      {seo?.jsonLd && !robots?.includes('noindex') && !noFollow && (
        <JsonLd data={seo.jsonLd} />
      )}
    </Head>
  );
};
